
import React from "react";

/**
 * BurnabyLicensing
 *
 * Renders a four‑step guide that explains how to obtain a short‑term‑rental
 * business licence in the City of Burnaby. The component is deliberately kept
 * simple and defensive:
 *   • All data is defined locally – no external dependencies.
 *   • Defensive checks are added before mapping over arrays to avoid runtime
 *     crashes if the structure ever changes.
 *   • Tailwind arbitrary values are wrapped in back‑ticks to keep the JSX
 *     parser happy.
 */
export default function BurnabyLicensing() {
  // -------------------------------------------------------------------------
  // Data – immutable, therefore safe to memoise if the component grows.
  // -------------------------------------------------------------------------
  const steps = [
    {
      number: "01",
      title: "Verify Eligibility",
      description:
        "Confirm your property is your principal residence and located in a permitted zone.",
      details: [
        "Principal residence documentation",
        "Zoning verification",
        "Strata approval if applicable",
      ],
    },
    {
      number: "02",
      title: "Gather Documentation",
      description:
        "Prepare all required documents including proof of residence and insurance.",
      details: [
        "Government-issued ID",
        "Proof of principal residence",
        "$2M liability insurance",
      ],
    },
    {
      number: "03",
      title: "Submit Application",
      description:
        "Complete the business license application through the City of Burnaby.",
      details: [
        "Online or in‑person submission",
        "Pay application fee",
        "Property inspection may be required",
      ],
    },
    {
      number: "04",
      title: "Receive License",
      description:
        "Once approved, display your license number on all rental listings.",
      details: [
        "License valid for one year",
        "Must be displayed on all platforms",
        "Annual renewal required",
      ],
    },
  ] as const;

  // -------------------------------------------------------------------------
  // Helper – safely renders a list of details.
  // -------------------------------------------------------------------------
  const renderDetails = (details: readonly string[]) => {
    if (!Array.isArray(details) || details.length === 0) return null;

    return (
      <ul className="space-y-2">
        {details.map((detail, idx) => (
          <li
            key={idx}
            className="flex items-start gap-2 text-sm text-[#505A5A]/60"
          >
            <i className="ri-check-line text-[#882629] mt-0.5"></i>
            {detail}
          </li>
        ))}
      </ul>
    );
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <section
      id="licensing"
      className="py-24 lg:py-32 bg-[#F8F8F8]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <span className="inline-block bg-[#882629]/10 text-[#882629] px-4 py-2 rounded-full text-sm font-medium mb-4">
            Licensing Process
          </span>
          <h2 className="font-['Fraunces'] text-4xl lg:text-5xl font-bold text-[#505A5A] mb-6">
            How to Get Licensed in Burnaby
          </h2>
          <p className="text-lg text-[#505A5A]/70 leading-relaxed">
            Follow these steps to obtain your short‑term rental business licence
            and operate legally in Burnaby.
          </p>
        </div>

        {/* Steps grid */}
        <div className="grid lg:grid-cols-4 gap-8">
          {Array.isArray(steps) && steps.length > 0 ? (
            steps.map((step, index) => (
              <div key={step.number} className="relative">
                <div className="bg-white rounded-2xl p-8 h-full shadow-sm hover:shadow-lg transition-all">
                  <div className="text-5xl font-bold text-[#882629]/20 mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-semibold text-[#505A5A] mb-3">
                    {step.title}
                  </h3>
                  <p className="text-[#505A5A]/70 mb-4 leading-relaxed">
                    {step.description}
                  </p>
                  {renderDetails(step.details)}
                </div>

                {/* Arrow connector (hidden on small screens) */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <i className="ri-arrow-right-line text-2xl text-[#882629]/30"></i>
                  </div>
                )}
              </div>
            ))
          ) : (
            <p className="col-span-4 text-center text-[#505A5A]/70">
              No steps available.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
